import { useEffect, useState } from "react";
import { useApi } from "~/api/provider";
import type { ReasoningApi, ReasoningEffort, ReasoningSupport } from "~/api/reasoningTypes";

/**
 * `GET /api/reasoning.support`, fetched once per API instance and shared by every reasoning
 * surface. The server publishes its limits (KnowIt.Web.Api/Controllers/ReasoningController.cs
 * `Support`); clients derive prose, caps and the branch-agent check from the answer instead of
 * retyping the numbers.
 */
const cache = new WeakMap<ReasoningApi, Promise<ReasoningSupport>>();

export function loadReasoningSupport(reasoning: ReasoningApi): Promise<ReasoningSupport> {
  let pending = cache.get(reasoning);
  if (!pending) {
    pending = reasoning.support().catch((error: unknown) => {
      cache.delete(reasoning); // A failed fetch is retried by the next mount, not remembered.
      throw error;
    });
    cache.set(reasoning, pending);
  }
  return pending;
}
export function resetReasoningSupportCache(reasoning?: ReasoningApi): void {
  if (reasoning) cache.delete(reasoning);
}

/**
 * Used only until `support()` has answered, or when it never does. The values mirror
 * `ReasoningProfile` (KnowIt.Application/Reasoning/ReasoningContracts.cs) and
 * `ReasoningController.Support`; the engine does not publish the parent/name caps, so those two
 * stay client constants and are named here once.
 */
export const REASONING_FALLBACK_LIMITS = {
  maxRecordBytes: 256 * 1024,
  maxIndependentBranches: 8,
  maxCandidateParents: 128,
  maxLogicalIdLength: 160,
  maxSearchCandidates: 0,
  maxSearchWidth: 0,
  maxSearchDepth: 0,
  maxRetainedArtifactBytes: 0,
} as const;

export interface ReasoningLimits {
  maxRecordBytes: number;
  maxIndependentBranches: number;
  maxCandidateParents: number;
  maxLogicalIdLength: number;
  maxSearchCandidates: number;
  maxSearchWidth: number;
  maxSearchDepth: number;
  maxRetainedArtifactBytes: number;
}

export function limitsOf(support: ReasoningSupport | null | undefined): ReasoningLimits {
  const read = (name: keyof ReasoningLimits) => {
    const value = support?.limits?.[name];
    return typeof value === "number" && Number.isFinite(value) && value > 0
      ? value
      : REASONING_FALLBACK_LIMITS[name];
  };
  return {
    maxRecordBytes: read("maxRecordBytes"),
    maxIndependentBranches: read("maxIndependentBranches"),
    maxCandidateParents: read("maxCandidateParents"),
    maxLogicalIdLength: read("maxLogicalIdLength"),
    maxSearchCandidates: read("maxSearchCandidates"),
    maxSearchWidth: read("maxSearchWidth"),
    maxSearchDepth: read("maxSearchDepth"),
    maxRetainedArtifactBytes: read("maxRetainedArtifactBytes"),
  };
}

/** Budgeted output caps per effort, in the server's order, or null until the server has said. */
export function budgetedLimitsOf(
  support: ReasoningSupport | null | undefined,
): Record<ReasoningEffort, number> | null {
  const limits = support?.localExecution?.budgetedOutputLimits;
  return limits && Object.keys(limits).length ? limits : null;
}

/** A reader's approximation of a token cap: about 0.78 words per token, rounded to hundreds. */
export const budgetedWords = (tokens: number): number =>
  Math.max(100, Math.round((tokens * 0.78) / 100) * 100);

export function useReasoningSupport() {
  const api = useApi();
  const [support, setSupport] = useState<ReasoningSupport | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    loadReasoningSupport(api.reasoning)
      .then((value) => {
        if (live) setSupport(value);
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [api]);
  return {
    support,
    error,
    limits: limitsOf(support),
    budgetedLimits: budgetedLimitsOf(support),
    branchAgents: support?.supportedBranchAgents ?? [],
  };
}
